import { Navigate } from "react-router-dom";
import RouteFallback from "../routes/RouteFallback";
import { usePermissions } from "./PermissionsContext";
import { checkRequirements } from "./utils";

/**
 * Route/UI gate. Renders children when the requirement is met.
 *
 * @param {{
 *   permission?: string | string[] | { module: string, action: string } | Array<string | { module: string, action: string }>,
 *   mode?: 'any' | 'all',
 *   fallback?: import('react').ReactNode,
 *   redirectTo?: string | null,
 *   waitWhileLoading?: boolean,
 *   children: import('react').ReactNode,
 * }} props
 *
 * Examples:
 *   <Can permission="mobile_units.create">...</Can>
 *   <Can permission={{ module: 'users', action: 'edit' }}>...</Can>
 *   <Can permission={[{ module: 'users', action: 'view' }, 'users.view_own']} mode="any">...</Can>
 */
export function Can({
  permission,
  mode = "any",
  fallback = null,
  redirectTo = null,
  waitWhileLoading = false,
  children,
}) {
  const { permissions, isLoading } = usePermissions();

  if (isLoading && waitWhileLoading) {
    return <RouteFallback />;
  }

  if (isLoading && !waitWhileLoading) {
    return fallback;
  }

  const allowed = checkRequirements(permissions, permission, mode);

  if (allowed) {
    return children;
  }

  if (redirectTo) {
    return <Navigate to={redirectTo} replace />;
  }

  return fallback;
}

/**
 * Route wrapper used by renderRoutes when a leaf route declares `permission`.
 */
export function RequirePermission({ permission, mode = "any", children }) {
  return (
    <Can
      permission={permission}
      mode={mode}
      redirectTo="/programs?program_type=field_session"
      waitWhileLoading
    >
      {children}
    </Can>
  );
}
